import type { SharedFromVenue } from './venue';
import type { QrType } from './qr-types';
import { QR_TYPES, QR_TYPE_INFO, entranceForQrType } from './qr-types';

/**
 * "What's New" banners — the picture a venue puts where the live vibes bar normally sits.
 *
 * The link is stored as a TYPE plus an id, never as a URL. Guest routes carry the
 * ordering mode (`/v/:slug/table-service/:table/…`, `/takeaway/…`, `/room-service/:location/…`),
 * so a stored URL would be frozen in whichever mode the owner was in and would drop a
 * guest out of their room-service session. The guest app rebuilds the route in the
 * guest's own mode from these two fields.
 */

/**
 * The one aspect ratio a What's New banner is ever shown at.
 *
 * SHARED so the dashboard cropper and the guest carousel cannot drift. Before this, the
 * owner uploaded any shape, the browser cropped it with `object-cover` at display time,
 * and the picture they had chosen was not the picture guests saw — heads cut off, text
 * out of frame. The cropper now enforces this ratio at upload, so what is framed is what
 * ships.
 *
 * 2:1 rather than 16:9: this is a promotional banner in a scrolling feed, the shape food
 * delivery apps use. It costs less vertical space on a phone and leaves the menu visible
 * beneath it.
 */
export const WHATS_NEW_ASPECT = 2;

/** Tailwind's form of {@link WHATS_NEW_ASPECT}. Keep the two in step. */
export const WHATS_NEW_ASPECT_CLASS = 'aspect-[2/1]';

/** A banner is a still or a short clip. */
export type WhatsNewMediaType = 'image' | 'video';

/**
 * What a banner points at. `event` is a bookable promotion — there is no events table.
 *
 * `external` is the one type that carries a URL instead of an id, and it is the only one
 * that leaves the app. The type+id rule above is about INTERNAL destinations: guest routes
 * are mode-prefixed, so a stored internal URL would strand a room-service guest. An
 * external destination has no id to resolve and no ordering mode to lose, so it is stored
 * as what it is — see `link_url`.
 */
export type WhatsNewLinkType = 'none' | 'menu' | 'menu_item' | 'event' | 'external';

export const WHATS_NEW_LINK_TYPES: { value: WhatsNewLinkType; label: string }[] = [
  { value: 'none', label: 'No link' },
  { value: 'menu', label: 'A menu' },
  { value: 'menu_item', label: 'A menu item' },
  { value: 'event', label: 'An event' },
  { value: 'external', label: 'A website' },
];

/** The one link type that leaves the app, and so the only one opened in a new tab. */
export function isExternalLink(linkType: WhatsNewLinkType): boolean {
  return linkType === 'external';
}

/**
 * http and https only — the check behind every external banner link.
 *
 * Shared because it is asserted in three places that must agree: the dashboard refuses to
 * save one, the API refuses to store one (via `httpUrl()`), and the guest app refuses to
 * render one. Any of the three disagreeing is how a `javascript:` URL ends up in an
 * `href` on a page where guests type card details.
 *
 * Parsed and compared exactly, not matched with a regex and not
 * `protocol.startsWith('http')` — the latter is true for `httpfoo:` as well.
 */
export function isHttpUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    // Not an absolute URL at all — a relative one would be read as an in-app path.
    return false;
  }
}

/** A banner as the owner manages it. */
export interface WhatsNewBanner {
  id: string;
  venue_id: string;
  /** The media URL — an image or a video, per `media_type`. */
  image_url: string;
  media_type: WhatsNewMediaType;
  /** Still frame for a video, generated at upload. Null for images. */
  poster_url: string | null;
  title: string | null;
  link_type: WhatsNewLinkType;
  /**
   * menus.id, menu_items.id or promotions.id, depending on `link_type`. Null when 'none'
   * and when 'external', which carries `link_url` instead.
   */
  link_id: string | null;
  /**
   * Absolute http(s) URL for `link_type === 'external'`, null for every other type.
   *
   * Validated with `httpUrl()` on the way in, never `z.string().url()`: zod's url()
   * accepts `javascript:` and `data:`, and this value is rendered as a link.
   */
  link_url: string | null;
  /** Which QR codes this banner shows on. All three by default — see WhatsNewPlacement. */
  qr_types: WhatsNewPlacement[];
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * A banner as the guest app receives it.
 *
 * `link_type`/`link_id` are already resolved server-side: if the linked menu, item or
 * event has been deleted or unpublished, they come back as `'none'`/`null` so the guest
 * sees the picture without a tap target rather than a card that leads nowhere.
 */
export interface PublicWhatsNewBanner {
  id: string;
  image_url: string;
  media_type: WhatsNewMediaType;
  poster_url: string | null;
  title: string | null;
  link_type: WhatsNewLinkType;
  link_id: string | null;
  /** Set only for `link_type === 'external'`. Always opened in a new tab. */
  link_url?: string | null;
  /**
   * The QR codes this banner shows on.
   *
   * Optional so a payload written before the column existed still parses, and absent
   * means all three — the same "absent is as it was" rule the rest of this file follows.
   */
  qr_types?: WhatsNewPlacement[] | null;
  /**
   * Set ONLY when this banner belongs to a different venue in the same group.
   *
   * Absent means "here", which is the common case. When present, `link_type` is `'none'`
   * or `'event'`, never a menu — a menu id does not resolve at another branch (enforced in
   * `whatsNew.service.ts assertShareable`). An event-linked banner opens that event in
   * place, exactly like one of this venue's own.
   */
  origin?: SharedFromVenue | null;
}

/**
 * Where a banner shows: the QR codes it appears on.
 *
 * Per BANNER, not per section. The section switches below stay exactly as they are — they
 * turn the whole What's New block on or off in a place — and this narrows within them, so
 * one banner can run in rooms only while another runs at the tables.
 *
 * Empty is never stored: a banner shows on all three by default and the owner unticks
 * what they do not want, which keeps every banner that existed before this feature
 * behaving exactly as it did.
 */
export type WhatsNewPlacement = QrType;

/**
 * Is this banner live on this QR code right now?
 *
 * Two independent gates, and both have to pass: the venue must run that QR code at all,
 * and the What's New section must be switched on for the place it belongs to. A banner
 * that passes neither is not broken — it is waiting for a switch, which is why
 * `whatsNewInactiveReason` explains it rather than the banner silently vanishing.
 */
export function bannerShowsAt(
  bannerQrTypes: readonly string[] | null | undefined,
  qrType: QrType,
  sectionOn: (entrance: 'main' | 'room_service') => boolean,
): boolean {
  // Absent means every QR code — a banner written before this column existed.
  const places = bannerQrTypes ?? QR_TYPES;
  if (!places.includes(qrType)) return false;
  return sectionOn(entranceForQrType(qrType));
}

/**
 * Why this banner is showing nowhere, in the owner's words. Null when it is live.
 *
 * The founder's call, and a better one than the alternatives considered: a banner whose
 * every place is switched off is not hidden and not refused at save time — it is listed
 * as INACTIVE with the reason, keeps all its settings, and comes back by itself the
 * moment the switch it is waiting on goes back on.
 *
 * Derived, never stored. Storing it would leave the banner dead after the owner fixed the
 * cause, which is the opposite of the point.
 */
export function whatsNewInactiveReason(
  bannerQrTypes: readonly string[] | null | undefined,
  runningQrTypes: readonly QrType[],
  sectionOn: (entrance: 'main' | 'room_service') => boolean,
): string | null {
  const places = (bannerQrTypes ?? QR_TYPES) as QrType[];
  const live = places.filter(p => runningQrTypes.includes(p) && sectionOn(entranceForQrType(p)));
  if (live.length > 0) return null;

  const notRunning = places.filter(p => !runningQrTypes.includes(p));
  if (notRunning.length === places.length) {
    const names = places.map(p => QR_TYPE_INFO[p].label).join(' and ');
    return `Set to ${names}, which this venue does not currently use.`;
  }

  const names = places.map(p => QR_TYPE_INFO[p].label).join(' and ');
  const where = places.every(p => entranceForQrType(p) === 'room_service') ? 'in rooms' : 'in the venue';
  return `Set to ${names}, but What's New is switched off ${where}.`;
}

/**
 * Which entrance a guest arrived through, for widget visibility.
 *
 * Only the room-service split matters: dine-in and takeaway share the "main QR", because
 * they are the same physical place with the same live occupancy. A guest in room 305 is
 * somewhere else entirely.
 */
export type GuestEntrance = 'main' | 'room_service';

/**
 * Should a landing-page widget show at this entrance?
 *
 * Shared so the guest app and the dashboard preview cannot disagree about it — the preview
 * exists to tell the owner what guests see, and a preview running its own copy of this rule
 * is worse than no preview.
 *
 * `!== false` on both, not `=== true`: a venue row saved before these columns existed has
 * neither field, and those venues were already showing both widgets. Absent must mean "as it
 * was", never "off".
 */
export function widgetVisibleAt(
  entrance: GuestEntrance,
  onMain: boolean | undefined,
  onRoomService: boolean | undefined,
): boolean {
  return entrance === 'room_service' ? onRoomService !== false : onMain !== false;
}
